import type { IRoof, IRoofAddress } from "../react-app-env";
import {roofTypesMap} from "../config/config";

export const defaultExtent: [number, number, number, number] = [420000, 30000, 900000, 350000];

export const defaultAddress: IRoofAddress = {
    id: 1,
    label: '',
    street: '',
    city: '',
    zip: '',
    state: '',
    roofs: [],
    searchAddress: null,
    currentInput: '',
    hasCustomRoof: false,
    roofType: roofTypesMap[0].type,
};

export function parseGeoAddress(address: any): IRoofAddress | null {
    // Extract the street name and number
    if (!address) return null;

    let attributes = address.attributes;
    const street = attributes.strname_deinr;
    // Extract the ZIP and City
    const zip = attributes.dplz4;
    let dplznames = attributes.dplzname.split(' ');
    const city = dplznames[0];

    // Extract the state
    const state = attributes.gdekt;
    return {
        id: 0,
        type: "geoadmin",
        label: street + ' ' + zip + ' ' + attributes.dplzname,
        street: street, // Street name with number
        zip: zip,           // ZIP code
        city: city,         // City
        state: state,        // State
        roofs: [],
        searchAddress: null,
        currentInput: '',
        hasCustomRoof: false,
        roofType: roofTypesMap[0].type,
    };
}

export function parseGeoRoof(roof: any): IRoof | null {
    if (!roof) return null;
    return {
        area: Math.round(roof.attributes?.flaeche ?? 0),
        slope: roof.attributes?.neigung,
        orientation: roof.attributes?.ausrichtung + 180,
        financialGain: Math.round(roof.attributes?.finanzertrag),
        summerElectricityYield_kWh: roof.attributes?.stromertrag_sommerhalbjahr,
        winterElectricityYield_kWh: roof.attributes?.stromertrag_winterhalbjahr,
        roofId: roof.featureId,
        geometry: roof.geometry,
        isCustom: false,
    };
}

export const getToleranceInPixels = function (toleranceMeters: any, mapExtent: any, display: any) {
    if (!toleranceMeters) {
        return 0;
    }
    let mapMeterWidth = Math.abs(mapExtent[0] - mapExtent[2]);
    let mapMeterHeight = Math.abs(mapExtent[1] - mapExtent[3]);
    let imgPixelWidth = display[0];
    let imgPixelHeight = display[1];
    let factor = Math.max(mapMeterWidth / imgPixelWidth, mapMeterHeight / imgPixelHeight);
    if (isFinite(factor) && !isNaN(factor)) {
        return Math.ceil(toleranceMeters / factor);
    }
    return 0;
};

// Function to calculate bounding extent from X and Y coordinates
const boundingExtentXY = (xs: number[], ys: number[]): ol.Extent => {
    let minX = Math.min(...xs);
    let minY = Math.min(...ys);
    let maxX = Math.max(...xs);
    let maxY = Math.max(...ys);
    return [minX, minY, maxX, maxY];
};

// Function to get bounding extent based on view parameters
export const getForViewAndSize = (
    center: [number, number],
    resolution: number,
    rotation: number,
    size: ol.Size
): ol.Extent => {
    let dx = (resolution * size[0]) / 2;
    let dy = (resolution * size[1]) / 2;
    let cosRotation = Math.cos(rotation);
    let sinRotation = Math.sin(rotation);

    let xs: number[] = [-dx, -dx, dx, dx];
    let ys: number[] = [-dy, dy, -dy, dy];

    for (let i = 0; i < 4; ++i) {
        let x = xs[i];
        let y = ys[i];
        xs[i] = center[0] + x * cosRotation - y * sinRotation;
        ys[i] = center[1] + x * sinRotation + y * cosRotation;
    }
    return boundingExtentXY(xs, ys);
};

export const getErrorMsg = function (error: any = null) {
    if (!navigator.geolocation) {
        return "Error: " + 'geolocation Not Supported';
    }
    return "Error: " + error.code;
};
